import { type StepperRef, type TranslationIDs, useIntl } from '@capsicum/common';
import { BaseCodeInput, BoldText, CondensedButton } from '@capsicum/common-web';
import { useCodeVerification } from '@capsicum/features/regulations/shared';
import styled from 'styled-components';

import { CODE_VERIFICATION_LENGTH } from '../../../../shared/src/hooks';

type CodeVerificationProps = {
	error?: string;
	onChangeTargetClicked?: () => void;
	onCodeSubmit: (code: string) => void;
	onResendPress: () => void;
	stepperRef: React.RefObject<StepperRef>;
	target: string;
	texts: {
		resendButtonText: string;
		timerMessage: TranslationIDs;
	};
};

export const CodeVerification = ({
	error: initialError,
	onChangeTargetClicked,
	onCodeSubmit,
	onResendPress,
	stepperRef,
	target,
	texts
}: CodeVerificationProps) => {
	const { $t } = useIntl();

	const { code, error, goToFirstStep, reminderCountdown, resendHandler, setCode } = useCodeVerification(
		onResendPress,
		stepperRef,
		initialError,
		onCodeSubmit
	);

	return (
		<S.CodeInputForm>
			<S.Title>{$t('auth.enterCode')}</S.Title>
			<S.Target>
				<S.Subtitle>
					{$t('auth.sentTo', {
						medium: <BoldText>{target}</BoldText>
					})}
				</S.Subtitle>
			</S.Target>
			<S.CodeInput
				codeLength={CODE_VERIFICATION_LENGTH}
				hasError={Boolean(error)}
				name="code-verification-input"
				testID="code-verification-input"
				value={code}
				onChange={setCode}
			/>
			{error && <S.CodeErrorText>{error}</S.CodeErrorText>}

			{reminderCountdown.seconds ? (
				<S.ResendCodeText>
					{$t(texts.timerMessage, {
						time: reminderCountdown.seconds?.toString().padStart(2, '0')
					})}
				</S.ResendCodeText>
			) : (
				<>
					<S.ResendCodeEnabledText>{$t('auth.welcome.codeInput.retrySMS')}</S.ResendCodeEnabledText>
					<S.ButtonWrapper>
						<CondensedButton
							color="neutral"
							key="resend-button"
							size="small"
							testID="code-verification-resend-button"
							text={texts.resendButtonText}
							variant="outlined"
							onClick={resendHandler}
						/>
					</S.ButtonWrapper>
				</>
			)}

			<S.Change
				onClick={() => {
					goToFirstStep();
					onChangeTargetClicked?.();
				}}
			>
				{$t('jurisdictionRequirements.mobileVerification.changePhoneNumber')}
			</S.Change>
		</S.CodeInputForm>
	);
};

namespace S {
	export const CodeInputForm = styled.div`
		display: flex;
		flex-direction: column;
	`;

	export const CodeInput = styled(BaseCodeInput)`
		justify-content: center;
		margin-bottom: ${({ theme }) => theme.sizing.medium};
	`;

	export const Target = styled.div`
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: center;
		justify-content: flex-start;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
		margin-bottom: ${({ theme }) => theme.sizing.xlarge};
	`;

	export const Change = styled.p`
		${({ theme }) => theme.fonts.text.text2};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		margin: ${({ theme }) => theme.sizing.xsmall} 0 0;
		cursor: pointer;
		text-align: center;
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const Subtitle = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const CodeErrorText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.error.enabled};
		margin-bottom: ${({ theme }) => theme.sizing.xsmall};
	`;

	export const ResendCodeText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.disabled};
	`;

	export const ResendCodeEnabledText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		margin-bottom: ${({ theme }) => theme.sizing.xxxsmall};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		justify-content: center;
	`;
}
